import { createPortal } from 'react-dom'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  onClose: () => void
  onCtaClick: () => void
  imageSrc: string
  imageAlt: string
  ctaLabel: string
}

/**
 * Presentational promo shell: full-bleed creative + primary CTA.
 * Intentionally not using shared Modal (title/header padding fights image layout).
 */
export default function PromotionModal({
  open,
  onClose,
  onCtaClick,
  imageSrc,
  imageAlt,
  ctaLabel,
}: Props) {
  const { t } = useTranslation('common')
  const ignoreBackdropUntilRef = useRef(0)

  useEffect(() => {
    if (!open) return
    ignoreBackdropUntilRef.current = Date.now() + 300

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  const handleBackdropClick = () => {
    if (Date.now() < ignoreBackdropUntilRef.current) return
    onClose()
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/60 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label={imageAlt}
    >
      <div
        className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-[#1a1a1a] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-1.5 text-white/90 hover:bg-black/70 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
          aria-label={t('close')}
        >
          <X className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={onCtaClick}
          className="block w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A00] focus-visible:ring-inset"
          aria-label={ctaLabel}
        >
          <img
            src={imageSrc}
            alt={imageAlt}
            className="h-auto w-full object-cover"
            loading="eager"
            decoding="async"
          />
        </button>

        <div className="flex items-center justify-end gap-3 border-t border-white/10 bg-[#141414] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white"
          >
            {t('promotions.dismiss')}
          </button>
          <button
            type="button"
            onClick={onCtaClick}
            className="rounded-lg bg-[#FF5A00] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#e04e00] focus:outline-none focus:ring-2 focus:ring-[#FF5A00]/50 focus:ring-offset-2 focus:ring-offset-[#141414]"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
